import { AffectedRecords, createProxyClient, TaskStatus } from '@extrahorizon/javascript-sdk';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { AffectedRecordsError } from 'services/errors/AffectedRecordsError';
import { appVersion, targetHost } from 'utils/app';
import { EmptyIdError } from 'services/errors/EmptyIdError';
import { ApiTaskResult } from 'models/general';
import { UpdateError } from 'services/errors/UpdateError';
import { handleQueryError } from './catchAll';

const SECONDS = 1000;
const MINUTES = 60 * SECONDS;

export const USER_QUERY_STALE_TIME = 5 * SECONDS;
export const MEASUREMENT_REFETCH_INTERVAL = 4 * MINUTES;
export const WORKLIST_REFETCH_INTERVAL = 30 * SECONDS;
export const DEFAULT_LIST_REFETCH_INTERVAL = 60 * SECONDS;
export const DEBOUNCE_TIME = 0.5 * SECONDS;

export const sdk = createProxyClient({
  host: targetHost,
  normalizeCustomData: true,
  headers: {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    'Fibricheck-User-Agent': `FibriCheck Portal/${appVersion} (${navigator.userAgent})`,
  },
});

export const RAW_ENDPOINTS = {
  PRESCRIPTIONS: '/prescriptions/v1',
  TASKS: '/tasks/v1',
  GROUPS: '/groups/v1',
  REPORTS: '/reports/v1',
};

export const API_FUNCTIONS = {
  monitoring: 'api-monitoring',
};

export const RULES_ENDPOINTS = {
  monitoringTemplates: 'monitoring-templates',
  monitoringRules: 'monitoring-rules',
  monitoringNotifications: 'monitoring-notifications',
};

export const SCHEMA_NAMES = {
  heartRhythm: 'fibricheck-measurements',
  measurementReports: 'measurement-reports',
  bloodPressureClassifications: 'blood-pressure-classification-guidelines',
  bloodPressureMeasurements: 'blood-pressure-measurements',
  physicianDashboardTableRows: 'portal-patients-list-records',
  patientMedicalEvents: 'patient-medical-events',
  patientMedicalData: 'patient-medical-data',
  usBillingRecords: 'us-billing-records',
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      networkMode: 'offlineFirst',
    },
    mutations: {
      networkMode: 'offlineFirst',
    },
  },
  queryCache: new QueryCache({
    onError: (error) => handleQueryError(error),
  }),
  mutationCache: new MutationCache({
    onError: (error) => handleQueryError(error),
  }),
});

export const queryKeys = {
  userData: 'userData',
  profileData: 'profileData',
  patientData: 'patientData',
  heartRhythmData: 'heartRhythmData',
  measurementWeightData: 'measuremenWeightData',
  patients: 'patients',
  packages: 'packages',
  staff: 'staff',
  reports: 'reports',
  reportsOverview: 'reportsOverview',
  patientReports: 'patientsReports',
  groupCredits: 'groupCredits',
  groupData: 'groupData',
  groupSettings: 'groupSettings',
  groupReportConfiguration: 'groupReportConfiguration',
  bloodPressureClassifications: 'bloodPressureClassifications',
  bloodPressureMeasurements: 'bloodPressureMeasurements',
  bloodPressureMeasurementComment: 'bloodPressureMeasurementComment',
  schemasInformation: 'schemasInformation',
  settings: 'settings',
  totalPatients: 'totalPatients',
  totalActivePatients: 'totalActivePatients',
  worklistOverview: 'worklistNotifications',
  worklistRecord: 'worklistRecord',
  reportPdf: 'reportPdf',
  measurementPdf: 'measurementPdf',
  worklistReportPdf: 'worklistReportPdf',
  totalAlert: 'totalAlert',
  localization: 'localization',
  measurementComments: 'measurementComments',
  measurementTotals: 'measurementTotals',
  unclaimMeasurements: 'unclaimMeasurements',
  nextMeasurementData: 'nextMeasurementData',
  userName: 'userName',
  groupLogging: 'groupLogging',
  heartRhythmMeasurement: 'heartRhythmMeasurement',
  patientMedicalEvents: 'patientMedicalEvents',
  logs: 'logs',
  patientName: 'patientName',
  passwordPolicy: 'passwordPolicy',
  patientRules: 'patientRules',
  groupRules: 'groupRules',
  ruleData: 'ruleData',
  monitoringNotifications: 'monitoringNotifications',
  billingRecords: 'billingRecords',
  users: 'users',
  profiles: 'profiles',
  generalConfiguration: 'generalConfiguration',
};

export const queryParams = {
  lastname: 'lastname',
  firstname: 'firstname',
  title: 'title',
  name: 'name',
  groups: 'groups',
  status: 'status',
  startDate: 'startDate',
  endDate: 'endDate',
  birthday: 'birthday',
  betweenDates: 'betweenDates',
  hasMeasurement: 'hasMeasurement',
  lastMeasurementResultType: 'lastMeasurementResultType',
  lastMostSevereMeasurementResultType: 'lastMostSevereMeasurementResultType',
  id: 'id',
  patientId: 'patientId',
  priority: 'priority',
  detailId: 'detailId',
  showRestricted: 'showRestricted',
  timestamp: 'timestamp',
  dateChoice: 'dateChoice',
};

export const assertAffectedRecord = (options: { result: AffectedRecords; expectedRecords?: number }) => {
  const { result, expectedRecords = 1 } = options;
  if (result.affectedRecords !== expectedRecords) {
    throw new AffectedRecordsError(result.affectedRecords, expectedRecords);
  }
  return true;
};

export const checkContainsId = (options: { result: { id?: string } }) => {
  if (!options.result.id) throw new EmptyIdError();
  return true;
};

export const checkIfGreatSuccess = (options: { result: ApiTaskResult }) => {
  return options.result.success;
};

export const assertStatus = (options: { status: number; expectedStatus?: number }) => {
  const { status, expectedStatus = 200 } = options;
  if (status !== expectedStatus) {
    throw new UpdateError();
  }
  return true;
};

export const assertCompleteTask = (options: { result: { status?: TaskStatus } }) => {
  const { result } = options;
  if (result.status !== 'complete') {
    throw new UpdateError();
  }
  return true;
};
